import Box from '@mui/material/Box';
import { Subscription } from '@rails/actioncable';
import { Job_Read, Ticket_Read } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { Job } from '~hooks/useJob/models';
import { Load } from '~hooks/useLoad/models';
import { Settlement } from '~hooks/useSettlements/models';
import { Ticket } from '~hooks/useTickets/models';
import TicketDrawer from '~pages/Approvals/Files/components/TicketDrawer/TicketDrawer';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import { subscribeToChannel } from '~utils/rtu/rtu-utils';
import {
  getEffectiveUserCompanyId,
  isViewerAccountCustomer,
} from '~utils/user/user-utils';

import SettlementLoadsQueryContext, {
  SettlementLoadsQueryContextProvider,
} from './SettlementLoadsQueryContext';
import SettlementLoadsTable from './SettlementLoadsTable/SettlementLoadsTable';

type TicketDrawerState = {
  job: Job;
  load: Load;
  loadNumber: number;
  ticket?: Ticket;
};

interface SettlementLoadsTabProps {
  settlement: Settlement;
}

export default function SettlementLoadsTabProvider({
  settlement,
}: SettlementLoadsTabProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  return (
    <SettlementLoadsQueryContextProvider companyId={companyId} settlement={settlement}>
      <SettlementLoadsTabContent />
    </SettlementLoadsQueryContextProvider>
  );
}

function SettlementLoadsTabContent() {
  const { userStore } = useStores();
  const userPermissions = userStore.getPermissions();

  const [ticketDrawer, setTicketDrawer] = useState<TicketDrawerState>();

  const {
    addTicket,
    clearQueryData,
    companyId,
    fetchJobById,
    isFetchingLoads,
    isFetchingTickets,
    jobsData,
    loadsData,
    refreshLoads,
    refreshLoadsAndTickets,
    settlement,
    ticketsData,
    updateStoredJob,
    updateStoredTicket,
  } = useContext(SettlementLoadsQueryContext);

  const isFetching = isFetchingLoads || isFetchingTickets;

  // Tickets are read-only when the viewer is the counterparty on the
  // settlement's account. Driver-only settlements have no account, so the
  // viewer can only be the settlement owner.
  const areTicketsReadOnly = isViewerAccountCustomer(
    userStore,
    settlement.customerAccount ?? settlement.vendorAccount,
  );
  const canCreateTicket = !areTicketsReadOnly && userPermissions.canCreateTicket;
  const canEditTicket = !areTicketsReadOnly && userPermissions.canEditTicket;

  const handleTableRowClick = useCallback(
    async (loadNumber: number, load: Load, ticket?: Ticket) => {
      const job = await fetchJobById(load.jobId);
      setTicketDrawer({ load, loadNumber, ticket, job });
    },
    [fetchJobById],
  );

  const handleRefreshTicket = useCallback(
    (ticket: Ticket, isNewTicket?: boolean) => {
      if (isNewTicket) {
        addTicket(ticket);
      } else {
        updateStoredTicket(ticket);
      }

      setTicketDrawer((state) => {
        if (!state) {
          return undefined;
        }

        if (isNewTicket || state.ticket?.id === ticket.id) {
          return { ...state, ticket };
        }

        return state;
      });
    },
    [addTicket, updateStoredTicket],
  );

  // The drawer holds its own copy of the selected load, so a refetch driven from inside it
  // updates the rows behind it but leaves the drawer showing pre-mutation state.
  useEffect(() => {
    setTicketDrawer((state) => {
      if (!state?.load) {
        return state;
      }

      const updatedLoad = loadsData.find((load) => load.id === state.load.id);

      if (!updatedLoad || updatedLoad === state.load) {
        return state;
      }

      return { ...state, load: updatedLoad };
    });
  }, [loadsData]);

  const [previousLoad, nextLoad] = useMemo(() => {
    if (!ticketDrawer?.load) {
      return [null, null];
    }

    const indexOfCurrentlySelectedLoadWithinAllLoads = loadsData.findIndex((load) => {
      return ticketDrawer.load.id === load.id;
    });

    const previousActionableLoad =
      indexOfCurrentlySelectedLoadWithinAllLoads > 0
        ? (loadsData
            .slice(0, indexOfCurrentlySelectedLoadWithinAllLoads)
            .reverse()
            .find((load) => {
              const ticket = ticketsData.find(({ loadId }) => loadId === load.id);
              const canOpenDrawerOnLoad = ticket ? canEditTicket : canCreateTicket;
              return canOpenDrawerOnLoad;
            }) ?? null)
        : null;

    const nextActionableLoad =
      indexOfCurrentlySelectedLoadWithinAllLoads < loadsData.length - 1
        ? (loadsData
            .slice(indexOfCurrentlySelectedLoadWithinAllLoads + 1)
            .find((load) => {
              const ticket = ticketsData.find(({ loadId }) => loadId === load.id);
              const canOpenDrawerOnLoad = ticket ? canEditTicket : canCreateTicket;
              return canOpenDrawerOnLoad;
            }) ?? null)
        : null;

    return [previousActionableLoad, nextActionableLoad];
  }, [canCreateTicket, canEditTicket, loadsData, ticketDrawer?.load, ticketsData]);

  const findTicketByLoadId = useCallback(
    (loadId: string) => {
      return ticketsData.find((ticket) => ticket.loadId === loadId);
    },
    [ticketsData],
  );

  // Subscribe to real-time ticket updates
  const ticketsRTUSubscription = useRef<Subscription | null>(null);
  const subscribeToTicketsRTU = useCallback(() => {
    subscribeToChannel(
      realTimeChannels.TicketUpdateChannel,
      companyId,
      (sub) => (ticketsRTUSubscription.current = sub),
      ({ data }: { data: Ticket_Read }) => {
        const parsedTicket = Ticket.parse(data);
        const isNewTicket =
          ticketsData.findIndex((ticket) => ticket.id === parsedTicket.id) === -1;

        if (isNewTicket) {
          addTicket(parsedTicket);
        } else {
          updateStoredTicket(parsedTicket);
        }
      },
    );
  }, [companyId, ticketsData, addTicket, updateStoredTicket]);

  useEffect(() => {
    if (!ticketsRTUSubscription.current) {
      subscribeToTicketsRTU();
    }

    return () => {
      ticketsRTUSubscription.current?.unsubscribe();
      ticketsRTUSubscription.current = null;
    };
  }, [subscribeToTicketsRTU]);

  // Subscribe to real-time job updates
  const jobsRTUSubscription = useRef<Subscription | null>(null);
  const subscribeToJobsRTU = useCallback(() => {
    subscribeToChannel(
      realTimeChannels.CompanyJobUpdateChannel,
      companyId,
      (sub) => (jobsRTUSubscription.current = sub),
      ({ data }: { data: Job_Read }) => {
        const parsedJob = Job.parse(data);
        const jobExistsInContext = jobsData.some((job) => job.id === parsedJob.id);

        if (jobExistsInContext) {
          updateStoredJob(parsedJob);
          refreshLoadsAndTickets();
        }
      },
    );
  }, [companyId, jobsData, updateStoredJob, refreshLoadsAndTickets]);

  useEffect(() => {
    if (!jobsRTUSubscription.current) {
      subscribeToJobsRTU();
    }

    return () => {
      jobsRTUSubscription.current?.unsubscribe();
      jobsRTUSubscription.current = null;
    };
  }, [subscribeToJobsRTU]);

  useEffect(() => {
    return () => {
      clearQueryData();
    };
  }, [clearQueryData]);

  if (isFetching) {
    return (
      <LoadingSpinner isVisible sx={{ px: 1, py: 2, bgcolor: 'background.paper' }} />
    );
  }

  if (loadsData.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          color: 'text.secondary',
          bgcolor: 'background.paper',
        }}
      >
        {t('common.no_data')}
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.paper' }}>
      <SettlementLoadsTable
        jobs={jobsData}
        loads={loadsData}
        onTableRowClick={handleTableRowClick}
        tickets={ticketsData}
      />

      {ticketDrawer && (
        <TicketDrawer
          job={ticketDrawer.job}
          load={ticketDrawer.load}
          loadNumber={ticketDrawer.loadNumber}
          onClose={() => setTicketDrawer(undefined)}
          onShowNextTicketClick={
            nextLoad
              ? () => {
                  const nextLoadTicket = findTicketByLoadId(nextLoad.id);
                  const nextLoadNumber =
                    loadsData.findIndex((load) => load.id === nextLoad.id) + 1;
                  handleTableRowClick(nextLoadNumber, nextLoad, nextLoadTicket);
                }
              : undefined
          }
          onShowPreviousTicketClick={
            previousLoad
              ? () => {
                  const previousLoadTicket = findTicketByLoadId(previousLoad.id);
                  const previousLoadNumber =
                    loadsData.findIndex((load) => load.id === previousLoad.id) + 1;
                  handleTableRowClick(
                    previousLoadNumber,
                    previousLoad,
                    previousLoadTicket,
                  );
                }
              : undefined
          }
          refreshLoads={refreshLoads}
          refreshTicket={handleRefreshTicket}
          ticket={ticketDrawer.ticket}
        />
      )}
    </Box>
  );
}
